<template>
  <div>
    <AlertMessage v-if="sectionState.feedInImport && !importAndFeedInMeters.length"
                  :variant="BootstrapVariant.INFO">
      <span v-html="tr('administration.powerFlow.NO_FEED_IN_IMPORT_METER_SELECTED')"></span>
    </AlertMessage>
    <AlertMessage v-else-if="hasUnselectedMeter"
                  :variant="BootstrapVariant.INFO"
                  :message="tr('administration.powerFlow.NO_METER_SELECTED_GRID')"></AlertMessage>
    <BFormGroup :content-cols="8"
                :label="tr('administration.powerFlow.POWER_EXCHANGE')"
                :label-cols="labelCols"
                class="mt-4"
                label-for="feed-in-import-radio"
                label-class="pt-0">
      <BFormRadio id="feed-in-import-radio"
                  v-model="type"
                  :value="GridType.FEED_IN_IMPORT"
                  aria-describedby="irradiance-source-feedback"
                  name="irradiance"
                  data-target="#grid">
        {{ tr('administration.powerFlow.FEED_IN_IMPORT') }}
      </BFormRadio>
      <BCollapse v-if="importAndFeedInMeters.length"
                 id="grid-collapse"
                 v-model="sectionState.feedInImport"
                 accordion="accordion-1">
        <BFormCheckbox v-model="isFeedInImportInstalled"
                       name="consumption"
                       class="ml-4 pt-2"
                       :class="{ 'pb-3': !isFeedInImportInstalled }">
          {{ tr('administration.powerFlow.FEED_IN_IMPORT_DESCRIPTION') }}
          <InfoIconWithTooltip>
            <p @click="onMeasurementOverviewDescriptionClicked"
               v-html="tr('administration.powerFlow.GRID_TOOLTIP',[`class='clickable link'`])"></p>
          </InfoIconWithTooltip>
        </BFormCheckbox>
        <BFormGroup v-if="isFeedInImportInstalled"
                    :content-cols="8"
                    :label="tr('common.METER_SINGULAR')"
                    :label-cols="2"
                    class="mt-3 ml-5"
                    label-for="feed-in-import-meter-selection">
          <Selection id="feed-in-import-meter-selection"
                     v-model="formMeters[GridType.FEED_IN_IMPORT]"
                     :label-cols="5"
                     :options="importAndFeedInMeters"
                     :close-on-select="true"
                     :preserve-search="true"
                     :placeholder="tr('common.METER_SINGULAR')"
                     :custom-label="descriptionWithAbbreviation"
                     track-by="abbreviation">
            <template #option="{ option }">
              <span class="text-truncate"
                    :title="descriptionWithAbbreviation(option)">{{ descriptionWithAbbreviation(option) }}</span>
            </template>
          </Selection>
        </BFormGroup>
      </BCollapse>

      <BFormRadio id="feed-in-radio"
                  v-model="type"
                  :value="GridType.FEED_IN"
                  aria-describedby="irradiance-source-feedback"
                  name="feed-in">
        {{ tr('administration.powerFlow.FEED_IN') }}
      </BFormRadio>
      <BCollapse v-if="feedInMeters.length"
                 id="feed-in-collapse"
                 v-model="sectionState.feedIn"
                 accordion="accordion-1">
        <BFormCheckbox v-model="isFeedInMeterInstalled"
                       class="ml-4 pt-2"
                       :class="{ 'pb-3': !isFeedInMeterInstalled }">
          {{ tr('administration.powerFlow.FEED_IN_METER_IS_INSTALLED') }}
          <InfoIconWithTooltip>
            <p @click="onMeasurementOverviewDescriptionClicked"
               v-html="tr('administration.powerFlow.GRID_TOOLTIP',[`class='clickable link'`])"></p>
          </InfoIconWithTooltip>
        </BFormCheckbox>
        <BFormGroup v-if="isFeedInMeterInstalled"
                    :content-cols="8"
                    :label="tr('common.METER_SINGULAR')"
                    :label-cols="2"
                    class="mt-3 ml-5"
                    label-for="feed-in-meter-selection">
          <Selection id="feed-in-meter-selection"
                     v-model="formMeters[GridType.FEED_IN]"
                     :label-cols="5"
                     :options="feedInMeters"
                     :close-on-select="true"
                     :preserve-search="true"
                     :placeholder="tr('common.METER_SINGULAR')"
                     :custom-label="descriptionWithAbbreviation"
                     track-by="abbreviation">
            <template #option="{ option }">
              <span class="text-truncate"
                    :title="descriptionWithAbbreviation(option)">{{ descriptionWithAbbreviation(option) }}</span>
            </template>
          </Selection>
        </BFormGroup>
      </BCollapse>

      <BFormRadio id="import-radio"
                  v-model="type"
                  :value="GridType.IMPORT"
                  aria-describedby="irradiance-source-feedback"
                  name="import">
        {{ tr('administration.powerFlow.IMPORT') }}
      </BFormRadio>
      <BCollapse v-if="importMeters.length"
                 id="import-collapse"
                 v-model="sectionState.import"
                 accordion="accordion-1">
        <BFormCheckbox v-model="isImportMeterInstalled"
                       class="ml-4 pt-2"
                       :class="{ 'pb-3': !isImportMeterInstalled }">
          {{ tr('administration.powerFlow.IMPORT_METER_IS_INSTALLED') }}
          <InfoIconWithTooltip>
            <p @click="onMeasurementOverviewDescriptionClicked"
               v-html="tr('administration.powerFlow.GRID_TOOLTIP',[`class='clickable link'`])"></p>
          </InfoIconWithTooltip>
        </BFormCheckbox>
        <BFormGroup v-if="isImportMeterInstalled"
                    :content-cols="8"
                    :label="tr('common.METER_SINGULAR')"
                    :label-cols="2"
                    class="mt-3 ml-5"
                    label-for="feed-in-meter-selection">
          <Selection id="feed-in-meter-selection"
                     v-model="formMeters[GridType.IMPORT]"
                     :label-cols="5"
                     :options="importMeters"
                     :close-on-select="true"
                     :preserve-search="true"
                     :placeholder="tr('common.METER_SINGULAR')"
                     :custom-label="descriptionWithAbbreviation"
                     track-by="abbreviation">
            <template #option="{ option }">
              <span class="text-truncate"
                    :title="descriptionWithAbbreviation(option)">{{ descriptionWithAbbreviation(option) }}</span>
            </template>
          </Selection>
        </BFormGroup>
      </BCollapse>

      <BFormRadio id="no-power-radio"
                  v-model="type"
                  :value="GridType.NONE"
                  aria-describedby="no-power-feedback"
                  name="no-power">
        {{ tr('administration.powerFlow.NO_POWER') }}
      </BFormRadio>
      <BCollapse id="no-power-collapse"
                 v-model="sectionState.none"
                 accordion="accordion-1">
      </BCollapse>
    </BFormGroup>
  </div>
</template>

<script>
  import intersectionBy from 'lodash/intersectionBy';
  import Selection from '@common/selection/Selection.vue';
  import InfoIconWithTooltip from '@common/layout/InfoIconWithTooltip.vue';
  import Abbreviation from '@common/const/Abbreviation';
  import AlertMessage from '@common/layout/AlertMessage.vue';
  import {
    BCollapse, BFormCheckbox, BFormGroup, BFormRadio,
  } from 'bootstrap-vue';
  import GridType from '../const/GridType';
  import BootstrapVariant from '~/zf/frontend/vendor/bootstrap/BootstrapVariant';
  import { translator } from '~/zf/frontend/common/mixins';

  export default {
    name: 'GridConfig',
    compatConfig: {
      MODE: 3,
    },
    components: {
      BCollapse,
      BFormRadio,
      BFormGroup,
      BFormCheckbox,
      AlertMessage,
      Selection,
      InfoIconWithTooltip,
    },
    expose: ['collectInputData'],
    mixins: [
      translator,
    ],
    inject: [
      'onMeasurementOverviewDescriptionClicked',
    ],
    props: {
      metersGroupedByAbbreviation: {
        type: Object,
        required: true,
      },
      labelCols: {
        type: Number,
        default: 2,
      },
      config: [Object, null],
    },
    data() {
      return {
        formMeters: {
          [GridType.FEED_IN_IMPORT]: null,
          [GridType.FEED_IN]: null,
          [GridType.IMPORT]: null,
        },
        type: true,
        isFeedInImportInstalled: true,
        isFeedInMeterInstalled: true,
        isImportMeterInstalled: true,
        GridType,
        BootstrapVariant,
      };
    },
    computed: {
      importAndFeedInMeters() {
        return intersectionBy(
          this.metersGroupedByAbbreviation[Abbreviation.ENERGY_INTERVAL_IMPORT],
          this.metersGroupedByAbbreviation[Abbreviation.ENERGY_INTERVAL_EXPORT],
          'id',
        );
      },
      importMeters() {
        return this.metersGroupedByAbbreviation[Abbreviation.ENERGY_INTERVAL_IMPORT]
          .concat(this.metersGroupedByAbbreviation[Abbreviation.ENERGY_INTERVAL]);
      },
      feedInMeters() {
        return this.metersGroupedByAbbreviation[Abbreviation.ENERGY_INTERVAL_EXPORT]
          .concat(this.metersGroupedByAbbreviation[Abbreviation.ENERGY_INTERVAL]);
      },
      hasUnselectedMeter() {
        return this.type !== GridType.NONE && !this.isMeterInstalled();
      },
      sectionState() {
        return {
          feedInImport: this.type === GridType.FEED_IN_IMPORT,
          feedIn: this.type === GridType.FEED_IN,
          import: this.type === GridType.IMPORT,
          none: this.type === GridType.NONE,
        };
      },
    },
    mounted() {
      [this.formMeters[GridType.FEED_IN_IMPORT]] = this.importAndFeedInMeters;
      [this.formMeters[GridType.FEED_IN]] = this.feedInMeters;
      [this.formMeters[GridType.IMPORT]] = this.importMeters;

      this.setConfig();
    },
    methods: {
      descriptionWithAbbreviation(option) {
        if (option.description) {
          return `${option.description} [${option.abbreviation}]`;
        }
        return option.abbreviation;
      },
      setConfig() {
        this.type = this.config ? Object.keys(this.config)[0] : GridType.NONE;

        switch (this.type) {
          case GridType.FEED_IN_IMPORT:
            this.isFeedInImportInstalled = !!this.config[this.type];
            if (this.isFeedInImportInstalled) {
              this.setFormMeters(this.importAndFeedInMeters);
            }
            break;
          case GridType.FEED_IN:
            this.isFeedInMeterInstalled = !!this.config[this.type];
            if (this.isFeedInMeterInstalled) {
              this.setFormMeters(this.feedInMeters);
            }
            break;
          case GridType.IMPORT:
            this.isImportMeterInstalled = !!this.config[this.type];
            if (this.isImportMeterInstalled) {
              this.setFormMeters(this.importMeters);
            }
            break;
          case GridType.NONE:
          default:
        }
      },
      setFormMeters(options) {
        this.formMeters[this.type] = options.find((meter) => meter.id === this.config[this.type]);
      },
      collectInputData() {
        if (this.type === GridType.NONE) {
          return { [GridType.NONE]: true };
        }
        return {
          [this.type]: this.isMeterInstalled() && this.formMeters[this.type] ? this.formMeters[this.type].id : null,
        };
      },
      isMeterInstalled() {
        const metersInstalled = {
          [GridType.FEED_IN_IMPORT]: this.isFeedInImportInstalled && this.importAndFeedInMeters.length,
          [GridType.FEED_IN]: this.isFeedInMeterInstalled && this.feedInMeters.length,
          [GridType.IMPORT]: this.isImportMeterInstalled && this.importMeters.length,
        };
        return metersInstalled[this.type];
      },
    },
  };
</script>
